import React, { useState, useEffect } from 'react';
import api from '../api';
import ConfirmDeleteModal from './ConfirmDeleteModal';

interface Package {
  id: number;
  name: string;
  code: string | null;
  length: number;
  width: number;
  height: number;
  weight: number;
  declared_value: number | null;
  description: string | null;
  is_active: boolean;
}

interface PackageManagerProps {
  userRole?: string;
}

const PackageManager: React.FC<PackageManagerProps> = ({ userRole = 'viewer' }) => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean; id: number | null; name: string}>({show: false, id: null, name: ''});
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    length: '',
    width: '',
    height: '',
    weight: '0.5',
    declared_value: '',
    description: '',
    is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const canEdit = userRole === 'admin' || userRole === 'manager';

  useEffect(() => { fetchPackages(); }, []);

  const fetchPackages = async () => {
    try {
      const res = await api.get('/packages/?active_only=false');
      setPackages(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch packages:', err);
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', code: '', length: '', width: '', height: '', weight: '0.5', declared_value: '', description: '', is_active: true });
    setEditingPackage(null);
    setShowForm(false);
  };

  const handleEdit = (pkg: Package) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      code: pkg.code || '',
      length: String(pkg.length),
      width: String(pkg.width),
      height: String(pkg.height),
      weight: String(pkg.weight),
      declared_value: pkg.declared_value ? String(pkg.declared_value) : '',
      description: pkg.description || '',
      is_active: pkg.is_active,
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.length || !formData.width || !formData.height) {
      alert('Name, Length, Width, and Height are required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        code: formData.code.trim() || null,
        length: parseFloat(formData.length),
        width: parseFloat(formData.width),
        height: parseFloat(formData.height),
        weight: parseFloat(formData.weight) || 0.5,
        declared_value: formData.declared_value ? parseFloat(formData.declared_value) : null,
        description: formData.description.trim() || null,
        is_active: formData.is_active,
      };

      if (editingPackage) {
        await api.put(`/packages/${editingPackage.id}`, payload);
      } else {
        await api.post('/packages/', payload);
      }

      resetForm();
      fetchPackages();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to save package');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    setDeleteConfirm({show: true, id, name});
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.id) return;
    try {
      await api.delete(`/packages/${deleteConfirm.id}`);
      setDeleteConfirm({show: false, id: null, name: ''});
      fetchPackages();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete package');
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading packages...</div>;
  }

  const handleSort = (field: string) => {
    if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const sortedPackages = [...packages].sort((a, b) => {
    if (!sortField) return 0;
    let aVal: any = (a as any)[sortField];
    let bVal: any = (b as any)[sortField];
    if (sortField === 'is_active') {
      aVal = aVal ? 1 : 0;
      bVal = bVal ? 1 : 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    }
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    const numFields = ['length', 'weight', 'declared_value'];
    if (numFields.includes(sortField)) {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    }
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 className="card-title">Packages</h2>
        {canEdit && (
          <button className="btn btn-primary btn-sm" onClick={() => { resetForm(); setShowForm(true); }}>
            + New Package
          </button>
        )}
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('code')} style={{ cursor: 'pointer' }}>Code {sortField === 'code' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('length')} style={{ cursor: 'pointer' }}>Dimensions (L x W x H) {sortField === 'length' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('weight')} style={{ cursor: 'pointer' }}>Weight (kg) {sortField === 'weight' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('declared_value')} style={{ cursor: 'pointer' }}>Declared Value {sortField === 'declared_value' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('is_active')} style={{ cursor: 'pointer' }}>Status {sortField === 'is_active' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              {canEdit && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sortedPackages.length === 0 ? (
              <tr>
                <td colSpan={canEdit ? 7 : 6} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                  No packages defined yet. Create your first package type.
                </td>
              </tr>
            ) : (
              sortedPackages.map(pkg => (
                <tr key={pkg.id}>
                  <td style={{ fontWeight: 600 }}>{pkg.name}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '12px', color: '#64748b' }}>{pkg.code || '-'}</td>
                  <td>{pkg.length} x {pkg.width} x {pkg.height} cm</td>
                  <td>{pkg.weight} kg</td>
                  <td>{pkg.declared_value ? `₹${pkg.declared_value}` : '-'}</td>
                  <td>
                    <span style={{
                      padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                      background: pkg.is_active ? '#d4edda' : '#f8d7da',
                      color: pkg.is_active ? '#155724' : '#721c24'
                    }}>
                      {pkg.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {canEdit && (
                    <td>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(pkg)} style={{ fontSize: '11px', padding: '3px 8px' }}>
                          Edit
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(pkg.id, pkg.name)} style={{ fontSize: '11px', padding: '3px 8px' }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => { if (!saving) resetForm(); }}>
          <div className="modal" style={{ maxWidth: '500px', position: 'relative' }} onClick={e => e.stopPropagation()}>
            <button onClick={() => { if (!saving) resetForm(); }}
              style={{ position: 'absolute', top: '12px', right: '16px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#64748b' }}
              aria-label="Close">×</button>

            <h3 style={{ marginBottom: '1rem' }}>{editingPackage ? 'Edit Package' : 'New Package'}</h3>

            <div style={{ display: 'grid', gap: '12px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Name *</label>
                  <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Small Box" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Code</label>
                  <input type="text" value={formData.code} onChange={e => setFormData({ ...formData, code: e.target.value })}
                    placeholder="e.g., SM" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                </div>
              </div>

              <div className="form-grid-3" style={{ gap: '10px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Length (cm) *</label>
                  <input type="number" value={formData.length} onChange={e => setFormData({ ...formData, length: e.target.value })}
                    placeholder="30" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Width (cm) *</label>
                  <input type="number" value={formData.width} onChange={e => setFormData({ ...formData, width: e.target.value })}
                    placeholder="20" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Height (cm) *</label>
                  <input type="number" value={formData.height} onChange={e => setFormData({ ...formData, height: e.target.value })}
                    placeholder="10" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                </div>
              </div>

              <div className="form-grid-2" style={{ gap: '10px', marginBottom: 0 }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Weight (kg)</label>
                  <input type="number" step="0.1" value={formData.weight} onChange={e => setFormData({ ...formData, weight: e.target.value })}
                    placeholder="0.5" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Declared Value (₹)</label>
                  <input type="number" value={formData.declared_value} onChange={e => setFormData({ ...formData, declared_value: e.target.value })}
                    placeholder="Optional" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                </div>
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Description</label>
                <input type="text" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={formData.is_active} onChange={e => setFormData({ ...formData, is_active: e.target.checked })} />
                Active
              </label>
            </div>

            <div style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving...' : editingPackage ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDeleteModal
        isOpen={deleteConfirm.show}
        title="Delete Package"
        message="Are you sure you want to delete this package? This action cannot be undone."
        itemName={deleteConfirm.name}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({show: false, id: null, name: ''})}
      />
    </div>
  );
};

export default PackageManager;
